import { AlertCircle, ArrowRight, Clock } from 'lucide-react'
import { formatDateTimeBR } from '@/utils/masks'

export interface ProtocoloAberto {
  _id: string
  numeroProtocolo: string
  nomeCompleto: string
  secretariaId?: string
  createdAt: string
}

interface AlertaAbertosProps {
  abertos: ProtocoloAberto[]
  secretariaNomeMap: Record<string, string>
  loading: boolean
  onVerTodos: () => void
}

export default function AlertaAbertos({ abertos, secretariaNomeMap, loading, onVerTodos }: AlertaAbertosProps) {
  const total = abertos.length

  if (loading) {
    return <div className="h-[132px] rounded-2xl bg-[#F7F8FA] animate-pulse" />
  }

  if (total === 0) {
    return (
      <div className="rounded-2xl border border-[#E7E9EC] bg-white px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#EAF7EE] flex items-center justify-center shrink-0">
          <Clock className="w-4 h-4 text-[#1E8A45]" strokeWidth={1.9} />
        </div>
        <div>
          <p className="text-[13.5px] font-semibold text-[#12141A]">Nenhum requerimento em aberto</p>
          <p className="text-[12px] text-[#6B7280] mt-0.5">Todos os pedidos recebidos já foram tratados.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-[#F3D9A4] bg-[#FFFCF4] overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-3.5 sm:py-4 border-b border-[#F3E4C4]">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative w-10 h-10 rounded-xl bg-[#FDF0D5] flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-[#B7791F]" strokeWidth={1.9} />
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D9622B] text-white text-[10.5px] font-semibold flex items-center justify-center tabular-nums">
              {total > 99 ? '99+' : total}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-[#12141A]">
              {total}
              {' '}
              {total === 1 ? 'novo requerimento em aberto' : 'novos requerimentos em aberto'}
            </p>
            <p className="text-[12px] text-[#8A6D3B] mt-0.5">Aguardando análise da equipa responsável.</p>
          </div>
        </div>
        <button
          onClick={onVerTodos}
          className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto self-start sm:self-auto px-3.5 py-2.5 sm:py-2 rounded-lg bg-[#12141A] text-white text-[12.5px] font-medium hover:bg-[#2A2E38] active:bg-[#2A2E38] transition-colors shrink-0"
        >
          Analisar agora
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>

      <div className="divide-y divide-[#F3E4C4]">
        {abertos.slice(0, 4).map(p => (
          <div key={p._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-3 px-4 sm:px-5 py-2.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D9622B] shrink-0" />
              <p className="text-[13px] text-[#12141A] truncate">
                <span className="font-medium">{p.nomeCompleto}</span>
                <span className="text-[#9CA3AF]">
                  {' '}
                  • Nº
                  {' '}
                  {p.numeroProtocolo}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0 pl-4 sm:pl-0">
              <span className="hidden sm:inline text-[11.5px] text-[#8A6D3B] truncate max-w-[180px]">
                {secretariaNomeMap[p.secretariaId || ''] || 'Sem secretaria'}
              </span>
              <span className="text-[11.5px] text-[#9CA3AF] tabular-nums">{formatDateTimeBR(p.createdAt)}</span>
            </div>
          </div>
        ))}
      </div>

      {total > 4 && (
        <div className="px-5 py-2.5 bg-[#FFF8E8] border-t border-[#F3E4C4]">
          <p className="text-[11.5px] text-[#8A6D3B]">
            e mais
            {' '}
            {total - 4}
            {' '}
            {total - 4 === 1 ? 'requerimento' : 'requerimentos'}
            {' '}
            em aberto.
          </p>
        </div>
      )}
    </div>
  )
}
